import * as React from "react";
import { Star, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";

const STORAGE_KEY = "lts-google-review-prompt";
const SHOW_DELAY_MS = 5000;
const REAPPEAR_DAYS = 30;
const REVIEW_URL = "https://g.page/r/CbZIbnVKWeQOEAE/review/";

type PromptState =
  | { status: "rated"; timestamp: number }
  | { status: "later"; timestamp: number }
  | null;

const readPromptState = (): PromptState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PromptState;
  } catch {
    return null;
  }
};

const writePromptState = (state: PromptState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors (e.g. Safari Private Mode)
  }
};

const shouldShowPrompt = (state: PromptState): boolean => {
  if (!state) return true;
  if (state.status === "rated") return false;
  const reappearMs = REAPPEAR_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - state.timestamp >= reappearMs;
};

const Stars = () => (
  <div className="mb-3 flex gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="h-4 w-4 text-rank-gold fill-current" aria-hidden="true" />
    ))}
  </div>
);

const PromptBody = ({
  onRate,
  onLater,
}: {
  onRate: () => void;
  onLater: () => void;
}) => (
  <>
    <Stars />
    <div className="space-y-2 text-left">
      <h2 className="text-lg font-bold text-foreground">Wie war dein Spiel?</h2>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Wir freuen uns riesig über eine kurze Bewertung bei Google – das hilft
        uns enorm und dauert nur 30 Sekunden.
      </p>
    </div>
    <div className="mt-6 flex flex-col gap-2">
      <Button
        className="h-11 w-full rounded-2xl bg-primary font-bold text-primary-foreground hover:bg-primary/90"
        onClick={onRate}
      >
        Jetzt bei Google bewerten
      </Button>
      <button
        type="button"
        className="w-full py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        onClick={onLater}
      >
        Vielleicht später
      </button>
    </div>
  </>
);


export function GoogleReviewPrompt() {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const hasRatedRef = React.useRef(false);
  const hasDismissedRef = React.useRef(false);

  React.useEffect(() => {
    const state = readPromptState();
    if (!shouldShowPrompt(state)) return;

    const timer = window.setTimeout(() => {
      setOpen(true);
    }, SHOW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, []);

  const handleRate = () => {
    hasRatedRef.current = true;
    writePromptState({ status: "rated", timestamp: Date.now() });
    window.open(REVIEW_URL, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const handleLater = () => {
    hasDismissedRef.current = true;
    writePromptState({ status: "later", timestamp: Date.now() });
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      if (!hasRatedRef.current && !hasDismissedRef.current) {
        handleLater();
        return;
      }
    }
    setOpen(nextOpen);
  };

  const contentClasses = "border border-border bg-card rounded-[20px] shadow-none";


  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent className={contentClasses}>
          <DrawerClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Schließen</span>
          </DrawerClose>
          <DrawerHeader className="pb-0">
            <DrawerTitle className="sr-only">
              Wie war dein Spiel?
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              Bitte hinterlasse uns eine kurze Google-Bewertung.
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 pt-2">
            <PromptBody onRate={handleRate} onLater={handleLater} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={contentClasses}>
        <DialogHeader>
          <DialogTitle className="sr-only">Wie war dein Spiel?</DialogTitle>
          <DialogDescription className="sr-only">
            Bitte hinterlasse uns eine kurze Google-Bewertung.
          </DialogDescription>
        </DialogHeader>
        <PromptBody onRate={handleRate} onLater={handleLater} />
      </DialogContent>
    </Dialog>
  );
}
